import React from 'react';
import {
  LayoutGrid, Folder as FolderIcon, Clock, Archive, Cloud, Plus, FolderOpen,
} from 'lucide-react';

/**
 * Left navigation (section 3/4) — Creator → Projects → Folders → Assets.
 * Purely a view-state switcher; doesn't fetch or hold data itself.
 */
export function MediaLibrarySidebar({
  creatorName, view, onViewChange, projects, selectedProjectId, onSelectProject,
  folders, selectedFolderId, onSelectFolder, onCreateFolder, archivedCount,
  archivedProjectIds = new Set(), archivedFolderIds = new Set(), onArchiveProject, onArchiveFolder,
}) {
  const navItem = (key, label, Icon, onClick, count) => (
    <button
      key={key} onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${view === key ? 'bg-[#3A7BFF]/15 text-white' : 'text-[#8A93A6] hover:bg-white/5 hover:text-white'}`}
      data-testid={`media-library-nav-${key}`}
    >
      <Icon className="h-4 w-4 shrink-0" /><span className="flex-1 truncate">{label}</span>
      {count != null && <span className="text-[10px] text-[#555E6E]">{count}</span>}
    </button>
  );

  const visibleProjects = projects.filter((p) => !archivedProjectIds.has(p.id));
  const visibleFolders = folders.filter((f) => !archivedFolderIds.has(f.id));

  return (
    <div className="w-60 shrink-0 flex flex-col gap-1 pr-2" data-testid="media-library-sidebar">
      <div className="px-3 py-2 mb-1">
        <p className="text-[10px] uppercase tracking-wide text-[#555E6E]">Creator</p>
        <p className="text-sm font-semibold text-white truncate" data-testid="media-library-active-creator">{creatorName}</p>
      </div>

      {navItem('all', 'All Media', LayoutGrid, () => onViewChange('all'))}
      {navItem('recent', 'Recent Assets', Clock, () => onViewChange('recent'))}

      <div className="mt-3 px-3 text-[10px] uppercase tracking-wide text-[#555E6E]">Projects</div>
      <div className="space-y-0.5 max-h-40 overflow-y-auto">
        {visibleProjects.map((p) => (
          <div key={p.id} className="group flex items-center">
            <button
              onClick={() => onSelectProject(p.id)}
              className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-left transition-colors ${view === 'project' && selectedProjectId === p.id ? 'bg-[#3A7BFF]/15 text-white' : 'text-[#8A93A6] hover:bg-white/5 hover:text-white'}`}
              data-testid={`media-library-nav-project-${p.id}`}
            >
              <FolderOpen className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{p.name}</span>
            </button>
            {onArchiveProject && (
              <button
                onClick={() => onArchiveProject(p.id, true)}
                className="opacity-0 group-hover:opacity-100 text-[#555E6E] hover:text-amber-400 px-1.5"
                title="Archive project (hides it from this Media Library only)"
                data-testid={`media-library-archive-project-${p.id}`}
              >
                <Archive className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
        {visibleProjects.length === 0 && <p className="text-xs text-[#555E6E] px-3">No projects yet.</p>}
      </div>

      <div className="flex items-center justify-between mt-3 px-3">
        <span className="text-[10px] uppercase tracking-wide text-[#555E6E]">Folders</span>
        <button onClick={onCreateFolder} className="text-[#8A93A6] hover:text-white" title="Create Folder" data-testid="media-library-create-folder-btn"><Plus className="h-3.5 w-3.5" /></button>
      </div>
      <div className="space-y-0.5 max-h-40 overflow-y-auto">
        {visibleFolders.map((f) => (
          <div key={f.id} className="group flex items-center">
            <button
              onClick={() => onSelectFolder(f.id)}
              className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-left transition-colors ${view === 'folder' && selectedFolderId === f.id ? 'bg-[#3A7BFF]/15 text-white' : 'text-[#8A93A6] hover:bg-white/5 hover:text-white'}`}
              data-testid={`media-library-nav-folder-${f.id}`}
            >
              <FolderIcon className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{f.name}</span>
            </button>
            {onArchiveFolder && (
              <button
                onClick={() => onArchiveFolder(f.id, true)}
                className="opacity-0 group-hover:opacity-100 text-[#555E6E] hover:text-amber-400 px-1.5"
                title="Archive folder (hides it from this Media Library only)"
                data-testid={`media-library-archive-folder-${f.id}`}
              >
                <Archive className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
        {visibleFolders.length === 0 && <p className="text-xs text-[#555E6E] px-3">No folders yet.</p>}
      </div>

      <div className="mt-3 border-t border-white/5 pt-2 space-y-0.5">
        {navItem('archived', 'Archived', Archive, () => onViewChange('archived'), archivedCount)}
        {navItem('backups', 'Backups', Cloud, () => onViewChange('backups'))}
      </div>
    </div>
  );
}
